import React from 'react'
import { Button, ButtonGroup, Col, Container, Row } from 'react-bootstrap'
import { Invoice } from '../../../../back-end/common/invoice'
import { Loading } from '../../components/ui/Loading/Loading'
import InvoiceInfoCard from '../../components/invoice/invoiceInfoCard'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { invoiceState } from '../../utilities/invoice'
import InvoiceCard from '../../components/invoice/invoiceCard'
import { canApprove, canDelete, canSendToContact, canSubmitForApproval, canVoid } from '../../components/invoice/invoiceAction'
import { XeroPayment } from '../../../../back-end/common/xeroPayment'
import { XeroAccount } from '../../../../back-end/common/xeroAccount'
import { XeroTaxRate } from '../../../../back-end/common/xeroTaxRate'
import InvoiceHistoryCard from '../../components/invoice/invoiceHistoryCard'
import { ExternalLink } from 'react-feather'
import { downloadReport, pollReportDownload } from '../reports/reportModal'

const InvoiceDetail = () => {
	const match = useMatch('/invoice/:id')
	const location = useLocation()
	const navigate = useNavigate()
	const invoiceId = match?.params.id || ''

	const { appState } = React.useContext(AppContext)
	const [invoice, setInvoice] = React.useState<Invoice | null>(null)
	const [payments, setPayments] = React.useState<XeroPayment[] | null>(null)
	const [accounts, setAccounts] = React.useState<XeroAccount[] | null>(null)
	const [taxRates, setTaxRates] = React.useState<XeroTaxRate[] | null>(null)
	const [invoiceReportLoading, setInvoiceReportLoading] = React.useState<boolean>(false)
	const [submitting, setSubmitting] = React.useState<boolean>(false)

	React.useEffect(() => {
		const getData = async () => {
			const [invoiceRes, paymentRes] = await Promise.all([
				Request.get(`invoice?where=InvoiceID==${invoiceId}`),
				Request.get(`payment?where=InvoiceID==${invoiceId}`),
			])
			const invoice = invoiceRes.data.invoices[0] as Invoice
			setInvoice(invoice)
			setPayments(paymentRes.data.xeroPayments)
			const [accountRes, ratesRes] = await Promise.all([
				Request.get(`xeroaccount?where=XeroOrgID==${invoice.xeroOrgID}`),
				Request.get(`xerotaxrate?where=XeroOrgID==${invoice.xeroOrgID}`),
			])
			setAccounts(accountRes.data.xeroAccounts)
			setTaxRates(ratesRes.data.xeroTaxRates)
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState])

	const handleSubmitApproval = React.useCallback(
		async (invoice: Invoice) => {
			const ogInvoice = JSON.parse(JSON.stringify(invoice))
			setSubmitting(true)
			try {
				await Request.put(`invoice/${invoice.invoiceID}`, { status: 'SUBMITTED' })
				await Request.sleep(4000) // this is purely just in case they press submit then approve straight away
				setInvoice({ ...invoice, status: 'SUBMITTED' })
			} catch (err) {
				console.error(err)
				setInvoice(ogInvoice)
			} finally {
				setSubmitting(false)
			}
		},
		[setInvoice, appState.authState]
	)

	const handleApprove = React.useCallback(
		async (invoice: Invoice) => {
			const ogInvoice = JSON.parse(JSON.stringify(invoice))
			setSubmitting(true)
			try {
				await Request.put(`invoice/${invoice.invoiceID}`, { status: 'AUTHORISED' })
				setInvoice({ ...invoice, status: 'AUTHORISED' })
			} catch (err) {
				console.error(err)
				setInvoice(ogInvoice)
			} finally {
				setSubmitting(false)
			}
		},
		[setInvoice, appState.authState]
	)

	const handleSendToClient = React.useCallback(
		async (invoice: Invoice, emailToClient: boolean) => {
			const ogInvoice = JSON.parse(JSON.stringify(invoice))
			setSubmitting(true)
			try {
				await Request.put(`invoice/${invoice.invoiceID}`, { sentToContact: true, emailToClient })
				setInvoice({ ...invoice, sentToContact: true })
			} catch (err) {
				console.error(err)
				setInvoice(ogInvoice)
			} finally {
				setSubmitting(false)
			}
		},
		[setInvoice, appState.authState]
	)

	const handleDelete = React.useCallback(
		async (invoice: Invoice) => {
			const ogInvoice = JSON.parse(JSON.stringify(invoice))
			try {
				await Request.put(`invoice/${invoice.invoiceID}`, { status: 'DELETED' })
				setInvoice({ ...invoice, status: 'DELETED' })
			} catch (err) {
				console.error(err)
				setInvoice(ogInvoice)
			}
		},
		[setInvoice, appState.authState]
	)

	const handleVoid = React.useCallback(
		async (invoice: Invoice) => {
			const ogInvoice = JSON.parse(JSON.stringify(invoice))
			try {
				await Request.put(`invoice/${invoice.invoiceID}`, { status: 'VOIDED' })
				setInvoice({ ...invoice, status: 'VOIDED' })
			} catch (err) {
				console.error(err)
				setInvoice(ogInvoice)
			} finally {
				setSubmitting(true)
			}
		},
		[setInvoice, appState.authState]
	)

	const handleViewPdf = React.useCallback(
		async (invoice: Invoice) => {
			const res = await Request.get(`invoice/${invoice.invoiceID}/pdf`, {
				responseType: 'blob',
				headers: { Accept: 'application/pdf' },
			})
			const blob = new Blob([res.data], { type: 'application/pdf' })
			const pdfUrl = URL.createObjectURL(blob)
			window.open(pdfUrl, '_blank', `__filename:${invoice.number}.pdf`)
		},
		[appState.authState]
	)

	const handleReportDownloadClick = async () => {
		if (!invoice) {
			return console.log('No Invoice')
		}
		setInvoiceReportLoading(true)
		const res = await Request.get(`billyreports/wipreport?invoiceContext=${invoice.invoiceID}&end=${invoice.date}`)

		if (res.data.success) {
			const filename = `${invoice.number}_${invoice.client?.name}_report.pdf`.replaceAll(/\s+/g, '_')
			const reportS3Location: string = res.data.reportS3Path
			const reportS3URL = await pollReportDownload(reportS3Location, filename)
			if (!reportS3URL) {
				console.log('No S3 URL Returned')
			} else {
				downloadReport(reportS3URL)
				setInvoiceReportLoading(false)
			}
		} else {
			console.log('Report generation failed')
		}
	}

	const handleGoTo = React.useCallback(async (url: string) => {
		navigate(url)
	}, [])

	if (invoice === null || payments === null || accounts === null || taxRates === null) {
		return (
			<div className="center-flex p-5">
				<Loading show />
			</div>
		)
	}

	const state = invoiceState(invoice.status, invoice.sentToContact)
	const role = appState.permission.roleID
	const canEdit = state === 'draft' || state === 'awaiting-approval'

	return (
		<Container fluid>
			<Row className="background-light align-items-center py-3 mb-3">
				<Col>
					<Row className="align-items-center">
						<Col sm={8}>
							<InvoiceInfoCard invoice={invoice} setInvoice={setInvoice} />
						</Col>
						<Col></Col>
						<Col sm="auto">
							<span>
								Status: <span className="span-bold">{state}</span>
							</span>
						</Col>
					</Row>
				</Col>
			</Row>
			<Row>
				{location.state && location.state.queryString && (
					<Col sm="auto">
						<Button onClick={() => navigate(`/invoices${location.state.queryString}`)}>Back to Invoices</Button>
					</Col>
				)}
				{invoice.status === 'DRAFT' && canSubmitForApproval.includes(role) && (
					<Col sm="auto">
						<Button onClick={() => handleSubmitApproval(invoice)} disabled={(invoice.lineItems?.length || 0) === 0 || submitting}>
							Submit for approval
						</Button>
					</Col>
				)}
				{(invoice.status === 'DRAFT' || invoice.status === 'SUBMITTED') && canApprove.includes(role) && (
					<Col sm="auto">
						<Button onClick={() => handleApprove(invoice)} disabled={(invoice.lineItems?.length || 0) === 0 || submitting}>
							Approve
						</Button>
					</Col>
				)}
				{invoice.status === 'AUTHORISED' && invoice.sentToContact === false && canSendToContact.includes(role) && (
					<>
						<Col sm="auto">
							<Button onClick={() => handleSendToClient(invoice, true)} disabled={submitting}>
								Email to Contact
							</Button>
						</Col>
						<Col sm="auto">
							<Button onClick={() => handleSendToClient(invoice, false)} disabled={submitting}>
								Mark as Sent
							</Button>
						</Col>
					</>
				)}
				<Col sm="auto">
					<ButtonGroup>
						<Button onClick={() => handleGoTo(`/invoice/${invoiceId}/allocation`)}>{canEdit ? 'Edit' : 'View'} Allocation</Button>
						<Button style={{ backgroundColor: 'var(--bs-primary)' }}>
							<a href={`/invoice/${invoiceId}/allocation`} target="_blank" rel="noreferrer" style={{ color: 'white' }}>
								<ExternalLink />
							</a>
						</Button>
					</ButtonGroup>
				</Col>
				<Col sm="auto">
					<Button onClick={() => handleGoTo(`/invoice/${invoiceId}/narration`)}>{canEdit ? 'Edit' : 'View'} Narration</Button>
				</Col>
				<Col sm="auto">
					<Button onClick={handleReportDownloadClick} disabled={invoiceReportLoading}>
						{invoiceReportLoading ? 'Generating PDF...' : 'WIP Report'}
					</Button>
				</Col>
				{(invoice.status === 'DRAFT' || invoice.status === 'SUBMITTED') && canDelete.includes(role) && (
					<Col sm="auto">
						<Button onClick={() => handleDelete(invoice)} disabled={submitting}>
							Delete
						</Button>
					</Col>
				)}
				{invoice.status === 'AUTHORISED' && canVoid.includes(role) && (
					<Col sm="auto">
						<Button onClick={() => handleVoid(invoice)} disabled={submitting}>
							Void
						</Button>
					</Col>
				)}
				{(invoice.status === 'AUTHORISED' || invoice.status === 'PAID') && (
					<Col sm="auto">
						<Button onClick={() => handleViewPdf(invoice)}>View PDF</Button>
					</Col>
				)}
			</Row>
			<Row>
				<Col>
					<InvoiceCard invoice={invoice} payments={payments} accounts={accounts} taxRates={taxRates} />
				</Col>
			</Row>
			<Row className="py-3">
				<Col>
					<InvoiceHistoryCard invoiceID={invoice.invoiceID} />
				</Col>
			</Row>
		</Container>
	)
}

export default InvoiceDetail
